import React, { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { 
  MessageSquare, 
  Send, 
  Upload, 
  Mic, 
  Menu, 
  X, 
  BookOpen, 
  Zap, 
  FolderOpen,
  Search,
  Clock,
  FileText,
  LogOut,
  User,
  TrendingUp,
  ChevronDown,
  Settings,
  UserCircle
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import DataConsentModal from "@/components/data-consent-modal";
import { reportTemplates } from "@/lib/report-templates";
import { useAuth } from "@/contexts/auth-context";
import { ThemeToggle } from "@/components/theme-toggle";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { PIC_MODEL_MAP, LEGACY_MODEL_MAP, getDefaultPICProfile } from "../config/pic_config";
import PICAnalysisDisplay from "@/components/PICAnalysisDisplay";
import FeedbackModal from "@/components/feedback-modal";

// AcronIQ Veritus Research Chat
// Fully fixed React component (TypeScript-ready)

// Utility function to clean up duplicate emojis in text
const cleanDuplicateEmojis = (text: string) => {
  // Simple approach: replace common duplicate emoji patterns
  // This targets the specific issue seen in the screenshot with 💡 💡
  return text
    .replace(/💡\s*💡+/g, '💡')
    .replace(/📊\s*📊+/g, '📊')
    .replace(/🔍\s*🔍+/g, '🔍')
    .replace(/⚡\s*⚡+/g, '⚡')
    .replace(/🎯\s*🎯+/g, '🎯')
    .replace(/💰\s*💰+/g, '💰')
    .replace(/📈\s*📈+/g, '📈')
    .replace(/🚀\s*🚀+/g, '🚀')
    .replace(/⭐\s*⭐+/g, '⭐')
    .replace(/✅\s*✅+/g, '✅');
};

export default function AcronIQVeritusResearchChat() {
  const [, setLocation] = useLocation();
  const { user, logout } = useAuth();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  
  // 🎯 FIX 4: Ref lock to prevent rapid submit spam
  const submittingRef = useRef(false);

  // Data consent management
  const [shouldShowModal, setShouldShowModal] = useState(false);

  const [messages, setMessages] = useState([{ 
    id: 1, 
    role: "system", 
    text: "Welcome to Veritus Intelligence! How can I help you today?", 
    time: new Date().toISOString() 
  }]);

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("balanced");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [picAnalysisVisible, setPicAnalysisVisible] = useState<Record<number, boolean>>({});

  const [chatHistory, setChatHistory] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  // STABILITY MODE: No local state tracking - server is source of truth

  // Debug function to check auth state
  const debugAuth = async () => {
    console.log('🔍 STABILITY: Debug auth state');
    const token = localStorage.getItem('acroniq_token');
    const user = localStorage.getItem('acroniq_user');
    
    console.log('🔍 STABILITY: LocalStorage state:', {
      hasToken: !!token,
      tokenLength: token?.length || 0,
      hasUser: !!user,
      user: user ? JSON.parse(user) : null
    });

    if (token) {
      try {
        const debugInfo = await apiRequest('GET', '/api/debug/auth');
        console.log('🔍 STABILITY: Server auth debug:', debugInfo);
      } catch (error) {
        console.error('🔍 STABILITY: Auth debug failed:', error);
      }
    }
  };

  // Add keyboard shortcut for debug (Ctrl+Shift+D)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        debugAuth();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (user && !conversationsLoaded) {
      loadConversations();
    }
  }, [user, conversationsLoaded]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages are added (if auto-scroll is enabled)
    if (autoScroll && messages.length > 1) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [messages, autoScroll]);

  // STABILITY MODE: Load conversations from server only
  const loadConversations = async () => {
    console.log('🔒 STABILITY: Loading conversations from server');
    try {
      const response = await apiRequest('GET', '/api/conversations') as any;
      console.log('🔒 STABILITY: Server response:', response);
      
      if (response.conversations) {
        const formattedHistory = response.conversations.map((conv: any) => ({
          id: conv.id,
          title: conv.title,
          preview: conv.metadata?.preview || 'No preview available',
          timestamp: new Date(conv.updated_at).toLocaleDateString(),
          messages: [] // Will be loaded when conversation is opened
        }));
        console.log(`✅ STABILITY: Loaded ${formattedHistory.length} conversations`);
        setChatHistory(formattedHistory);
      } else {
        console.log('⚠️ STABILITY: No conversations in response');
        setChatHistory([]);
      }
      setConversationsLoaded(true);
    } catch (error) {
      console.error('❌ STABILITY: Failed to load conversations:', error);
      setChatHistory([]);
      setConversationsLoaded(true);
    }
  };

  // STABILITY MODE: Simple AI generation with immediate save
  const generateMutation = useMutation({
    mutationFn: async ({ prompt, mode, conversationId }: { prompt: string; mode: string; conversationId: string }) => {
      // 🎯 STABILITY: Block generate unless conversationId exists
      if (!conversationId) {
        throw new Error("No conversation - cannot generate response");
      }
      
      const response = await apiRequest('POST', '/api/generate', {
        prompt,
        mode
      }) as any;
      // Return conversationId with response for onSuccess
      return { ...response, _conversationId: conversationId };
    },
    onSuccess: async (data: any) => {
      const aiResponseText = cleanDuplicateEmojis(data.response || data.message || "I apologize, but I couldn't generate a response.");
      const convId = data._conversationId;
      
      // Add AI response to local state immediately (don't reload from server)
      const aiMessageObj = {
        id: Date.now() + 1,
        role: "assistant",
        text: aiResponseText,
        time: new Date().toISOString(),
        meta: { mode: data.mode }
      };
      setMessages((prev: any) => [...prev, aiMessageObj]);
      
      // Save AI response to database in background (pass conversationId directly)
      try {
        saveMessage('assistant', aiResponseText, {
          mode: data.mode,
          processingTime: data.processingTime,
          tokensUsed: data.tokensUsed
        }, convId);
        // 🎯 STABILITY: NO loadChat() - keep local state, don't overwrite
      } catch (error) {
        console.error('❌ STABILITY: Failed to save AI response:', error);
      }
      
      setLoading(false);
    },
    onError: (error: any) => {
      console.error('Generation error:', error);
      const errorResponse = {
        id: Date.now() + 1,
        role: "assistant",
        text: "I apologize, but I encountered an error while processing your request. Please try again.",
        time: new Date().toISOString(),
        meta: { mode: "error" }
      };
      setMessages((prev: any) => [...prev, errorResponse]);
      setLoading(false);
    }
  });

  // File Upload Handler
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      console.log('File selected:', file.name);
      // TODO: Implement file processing
      alert(`File "${file.name}" selected. File processing will be implemented in a future update.`);
    }
  };

  // Voice Input Handler
  const handleVoiceInput = () => {
    if (isListening) {
      setIsListening(false);
      console.log('Voice input stopped');
    } else {
      setIsListening(true);
      console.log('Voice input started');
      // TODO: Implement voice recognition
      setTimeout(() => {
        setIsListening(false);
        alert('Voice input will be implemented in a future update.');
      }, 2000);
    }
  };

  // Smart Prompts Library
  const smartPrompts = [
    {
      id: 1,
      title: "Market Analysis",
      prompt: "Analyze the competitive landscape for AI-powered fintech startups in the UK market, focusing on regulatory challenges and growth opportunities.",
      category: "Market Research"
    },
    {
      id: 2,
      title: "Investment Thesis",
      prompt: "Create a comprehensive investment thesis for a SaaS startup targeting small businesses, including market size, competitive advantages, and financial projections.",
      category: "Investment"
    },
    {
      id: 3,
      title: "Product Strategy",
      prompt: "Develop a go-to-market strategy for a B2B AI platform, including target customer segments, pricing models, and distribution channels.",
      category: "Strategy"
    },
    {
      id: 4,
      title: "Risk Assessment",
      prompt: "Conduct a comprehensive risk analysis for entering the European renewable energy market, covering regulatory, financial, and operational risks.",
      category: "Risk Analysis"
    },
    {
      id: 5,
      title: "Technology Trends",
      prompt: "Analyze emerging technology trends in quantum computing and their potential impact on cybersecurity over the next 5 years.",
      category: "Technology"
    },
    {
      id: 6,
      title: "Customer Research",
      prompt: "Design a customer research framework to understand pain points and preferences for enterprise software buyers in the healthcare sector.",
      category: "Research"
    },
    {
      id: 7,
      title: "Business Model",
      prompt: "Evaluate different business models for a sustainability-focused marketplace platform, comparing subscription vs. commission-based approaches.",
      category: "Business Model"
    },
    {
      id: 8,
      title: "Global Expansion",
      prompt: "Create an expansion plan for a UK-based fintech company looking to enter Asian markets, focusing on regulatory requirements and local partnerships.",
      category: "Expansion"
    }
  ];

  // STABILITY MODE: Simple message submission with immediate save
  async function handleSubmit(e?: React.FormEvent) {
    e && e.preventDefault();
    if (!input.trim() || loading) return;

    // 🎯 FIX 4: Prevent rapid submit spam with ref lock
    if (submittingRef.current) {
      console.warn("🚫 Already submitting - ignoring rapid click");
      return;
    }
    submittingRef.current = true;

    const userMessage = input.trim();
    setInput("");
    setLoading(true);

    console.log('🔒 STABILITY: Submitting message:', userMessage);

    // Add user message to UI immediately for better UX
    const userMessageObj = {
      id: Date.now(),
      role: "user",
      text: userMessage,
      time: new Date().toISOString()
    };
    setMessages((prev: any) => [...prev, userMessageObj]);

    try {
      // Create conversation if needed
      let conversationId = currentConversationId;
      if (!conversationId) {
        console.log('🔒 STABILITY: Creating new conversation');
        const response = await apiRequest('POST', '/api/conversations', {
          title: userMessage.slice(0, 50) + (userMessage.length > 50 ? '...' : ''),
          metadata: { preview: userMessage.slice(0, 100) }
        }) as any;
        conversationId = response.conversation?.id;
        
        // 🎯 FIX 2: Hard-fail if conversation creation fails
        if (!conversationId) {
          throw new Error("Conversation creation failed - no ID returned");
        }
        
        setCurrentConversationId(conversationId);
        console.log('✅ STABILITY: Conversation created:', conversationId);
        
        // 🎯 STABILITY: Don't reload conversations - causes state overwrites
        // loadConversations() will be called on next page load or manual refresh
      }

      // Save user message to backend (pass conversationId directly - don't rely on state)
      await saveMessage('user', userMessage, {}, conversationId);
      
      // Generate AI response
      const picMode = LEGACY_MODEL_MAP[mode as keyof typeof LEGACY_MODEL_MAP] || 'pic-mini';
      generateMutation.mutate({ 
        prompt: userMessage, 
        mode: picMode,
        conversationId
      });
      
    } catch (error) {
      console.error('❌ STABILITY: Failed to submit message:', error);
      setLoading(false);
    } finally {
      // 🎯 FIX 4: Always release the submission lock
      submittingRef.current = false;
    }
  }

  // STABILITY MODE: Simple message saving - server handles deduplication
  const saveMessage = async (role: string, content: string, metadata = {}, convId?: string) => {
    const targetConversationId = convId || currentConversationId;
    if (!targetConversationId) {
      console.warn('🚫 STABILITY: No conversation ID - cannot save message');
      return;
    }
    
    console.log(`🔒 STABILITY: Saving ${role} message to conversation ${targetConversationId}`);
    try {
      const response = await apiRequest('POST', `/api/conversations/${targetConversationId}/messages`, {
        role,
        content,
        metadata,
        client_message_id: `frontend-${Date.now()}-${Math.random()}`
      });
      console.log('✅ STABILITY: Message saved:', response);
      return response;
    } catch (error) {
      console.error('❌ STABILITY: Failed to save message:', error);
      throw error;
    }
  };

  // STABILITY MODE: Load conversation from server only
  const loadChat = async (chat: any) => {
    console.log(`🔒 STABILITY: Loading conversation ${chat.id}`);
    try {
      const response = await apiRequest('GET', `/api/conversations/${chat.id}/messages`) as any;
      console.log('🔒 STABILITY: Messages response:', response);
      
      if (response && response.messages) {
        const formattedMessages = [
          { id: 0, role: "system", text: "Welcome to Veritus Intelligence! How can I help you today?", time: new Date().toISOString() },
          ...response.messages.map((msg: any, index: number) => ({
            id: index + 1,
            role: msg.role,
            text: msg.content,
            time: msg.created_at,
            meta: msg.metadata
          }))
        ];
        console.log(`✅ STABILITY: Loaded ${response.messages.length} messages`);
        setMessages(formattedMessages);
        setCurrentConversationId(chat.id);
      } else {
        console.log('⚠️ STABILITY: No messages in response');
        setMessages([{ id: 0, role: "system", text: "Welcome to Veritus Intelligence! How can I help you today?", time: new Date().toISOString() }]);
        setCurrentConversationId(chat.id);
      }
    } catch (error) {
      console.error('❌ STABILITY: Failed to load conversation:', error);
      // Fallback to empty conversation
      setMessages([{ id: 1, role: "system", text: "Welcome to Veritus Intelligence! How can I help you today?", time: new Date().toISOString() }]);
    }
    setTimeout(() => scrollToBottom(), 100);
  };

  const startNewChat = () => {
    // 🎯 STABILITY: No conversation creation here - handleSubmit owns it exclusively
    console.log('🔒 STABILITY: Starting new chat - no server calls');
    
    const newMessages = [
      {
        id: Date.now(),
        role: "system",
        text: "👋 Hi — I'm your Research Analyst. Ask anything about markets, business ideas, or strategy. Try: 'Find a scalable product idea for 2026.'",
        time: new Date().toISOString(),
      }
    ];
    setMessages(newMessages);
    setCurrentConversationId(null); // 🎯 Server IDs only - no "current" logic
    setInput("");
    setLoading(false);
    setAutoScroll(true);
    setSidebarOpen(false);
    scrollToBottom();
  };

  const clearChatHistory = async () => {
    if (confirm("Are you sure you want to clear all chat history? This cannot be undone.")) {
      try {
        // Delete all conversations from Supabase
        for (const chat of chatHistory) {
          await apiRequest('DELETE', `/api/conversations/${(chat as any).id}`);
        }
        setChatHistory([]);
        startNewChat(); // 🎯 STABILITY: No await - no server calls
      } catch (error) {
        console.error('Failed to clear chat history:', error);
        // Fallback to local clear
        setChatHistory([]);
        startNewChat(); // 🎯 STABILITY: No server calls
      }
    }
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
      setAutoScroll(true);
    }
  };

  // Utility function to check if user is at bottom of chat
  const isUserAtBottom = () => {
    if (!chatContainerRef.current) return false;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    return scrollTop + clientHeight >= scrollHeight - 20;
  };

  // Utility function to scroll to bottom if auto-scroll is enabled
  const conditionalScrollToBottom = () => {
    if (autoScroll && scrollRef.current) {
      setTimeout(() => {
        scrollRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 50);
    }
  };


  // 🎯 STABILITY: Removed "current" chat logic - server IDs only

  return (
    <>
      {/* Data Consent Modal */}
      {shouldShowModal && <DataConsentModal isOpen={shouldShowModal} onClose={() => setShouldShowModal(false)} onConsent={() => setShouldShowModal(false)} />}

      <div className="h-screen bg-background text-foreground flex overflow-hidden">
        {/* Enhanced Sidebar - Responsive */}
        <div className={`${sidebarOpen ? "w-80 sm:w-80 md:w-80" : "w-0"} transition-all duration-300 overflow-hidden bg-sidebar border-r border-sidebar-border flex-shrink-0 relative z-10 ${!sidebarOpen ? 'hidden' : ''} ${sidebarOpen ? 'fixed sm:relative inset-y-0 left-0 z-50 sm:z-10' : ''}`}>
          <div className="flex flex-col h-full">
            {/* Sidebar Header - Simplified */}
            <div className="flex items-center justify-between p-4">
              <button
                onClick={startNewChat}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-sidebar-accent transition-colors text-sm font-medium"
              >
                <MessageSquare className="w-4 h-4" />
                New chat
              </button>
              <button 
                onClick={() => setSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-sidebar-accent text-muted-foreground hover:text-foreground transition-colors"
                title="Close sidebar"
                aria-label="Close sidebar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Search */}
            <div className="px-4 py-2">
              <button
                onClick={() => setShowSearchModal(true)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-sidebar-accent/50 hover:bg-sidebar-accent border border-sidebar-border hover:border-border transition-all text-left group"
              >
                <Search className="w-4 h-4 text-muted-foreground group-hover:text-sidebar-foreground" />
                <span className="text-muted-foreground group-hover:text-sidebar-foreground text-sm">Search chats...</span>
                <div className="ml-auto hidden sm:flex items-center gap-1 text-xs text-muted-foreground">
                  <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">⌘</kbd>
                  <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">K</kbd>
                </div>
              </button>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto sidebar-scroll">
              {/* Navigation Section (Mobile) */}
              <div className="px-4 py-3 md:hidden border-b border-sidebar-border">
                <div className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground uppercase tracking-wide font-semibold">
                  <Menu className="w-3 h-3" />
                  Navigation
                </div>
                <div className="mt-2 space-y-1">
                  <button 
                    onClick={() => setLocation("/signals")}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-sidebar-accent rounded-md transition-colors text-sm group"
                  >
                    <div className="w-6 h-6 rounded bg-primary/20 flex items-center justify-center">
                      <Zap className="w-3 h-3 text-primary" />
                    </div>
                    <span className="group-hover:text-foreground">Signals</span>
                  </button>
                </div>
              </div>

              {/* Library Section */}
              <div className="px-4 py-3">
                <div className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground uppercase tracking-wide font-semibold">
                  <BookOpen className="w-3 h-3" />
                  Library
                </div>
                <div className="mt-2 space-y-1">
                  <button 
                    onClick={() => {
                      setLocation("/smart-prompts");
                      setSidebarOpen(false); // Close sidebar when navigating
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-sidebar-accent rounded-md transition-colors text-sm group"
                  >
                    <div className="w-6 h-6 rounded bg-primary/20 flex items-center justify-center">
                      <Zap className="w-3 h-3 text-primary" />
                    </div>
                    <span className="group-hover:text-foreground">Smart Prompts</span>
                    <span className="ml-auto text-xs text-muted-foreground">8</span>
                  </button>
                  <button 
                    onClick={() => {
                      setLocation("/templates");
                      setSidebarOpen(false); // Close sidebar when navigating
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-sidebar-accent rounded-md transition-colors text-sm group"
                  >
                    <div className="w-6 h-6 rounded bg-primary/20 flex items-center justify-center">
                      <FolderOpen className="w-3 h-3 text-primary" />
                    </div>
                    <span className="group-hover:text-foreground">Templates</span>
                    <span className="ml-auto text-xs text-muted-foreground">5</span>
                  </button>
                </div>
              </div>

              {/* Recent Chats */}
              <div className="px-4 py-3">
                <div className="flex items-center justify-between px-2 py-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide font-semibold">
                    <Clock className="w-3 h-3" />
                    Recent Chats
                  </div>
                  {chatHistory.length > 0 && (
                    <button
                      onClick={clearChatHistory}
                      className="text-xs text-muted-foreground hover:text-destructive transition-colors"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="mt-2 space-y-1">
                  {(() => {
                    const filteredChats = chatHistory.filter((chat: any) => 
                      chat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      chat.preview.toLowerCase().includes(searchQuery.toLowerCase())
                    );
                    
                    if (filteredChats.length === 0) {
                      return (
                        <div className="text-xs text-muted-foreground text-center py-8 px-4">
                          <MessageSquare className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                          {searchQuery ? "No chats match your search" : "No chats yet. Start a new conversation!"}
                        </div>
                      );
                    }
                    
                    return filteredChats.map((chat: any) => (
                      <button
                        key={chat.id}
                        onClick={() => loadChat(chat)}
                        className="w-full flex flex-col items-start gap-1 px-3 py-3 hover:bg-sidebar-accent rounded-md transition-colors text-sm group border border-transparent hover:border-sidebar-border"
                      >
                        <div className="font-medium text-left group-hover:text-foreground truncate w-full">{chat.title}</div>
                        <div className="text-muted-foreground text-xs text-left truncate w-full">{chat.preview}</div>
                        <div className="text-muted-foreground text-xs">{chat.timestamp}</div>
                      </button>
                    ));
                  })()}
                </div>
              </div>
            </div>

            {/* Sidebar Footer */}
            <div className="p-4 border-t border-sidebar-border bg-sidebar/50">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>Veritus Active</span>
                <span className="ml-auto">{chatHistory.length} chats</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Chat Area - Responsive */}
        <div className="flex-1 flex flex-col min-w-0 relative">
          {/* Overlay for mobile sidebar */}
          {sidebarOpen && (
            <div 
              className="fixed inset-0 bg-black/50 z-40 sm:hidden" 
              onClick={() => setSidebarOpen(false)}
            />
          )}
          
          {/* Header - Responsive */}
          <header className="bg-background/95 backdrop-blur-sm border-b border-border px-3 sm:px-4 py-3 flex items-center justify-between sticky top-0 z-20">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Toggle sidebar"
                aria-label="Toggle sidebar"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 sm:gap-3">
                <img
                  src="/veritus-icon.png"
                  alt="Veritus"
                  className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover"
                />
                <div className="hidden sm:block">
                  <h1 className="font-semibold text-foreground">Veritus Intelligence</h1>
                  <p className="text-xs text-muted-foreground">Strategic Analysis Engine</p>
                </div>
                <div className="sm:hidden">
                  <h1 className="font-semibold text-foreground text-sm">Veritus</h1>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Navigation */}
              <div className="hidden md:flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLocation("/signals")}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Signals
                </Button>
              </div>

              {/* Mode Selector */}
              <div className="flex items-center gap-2">
                <div className="text-xs text-muted-foreground">Mode:</div>
                <div className="px-3 py-1 rounded-full bg-muted text-sm capitalize border border-border">
                  {mode.replace("-", " ")}
                </div>
              </div>

              {/* Feedback Button */}
              <button
                onClick={() => setShowFeedbackModal(true)}
                className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Give Feedback"
                aria-label="Give Feedback"
              >
                <MessageSquare className="w-4 h-4" />
              </button>

              {/* Theme Toggle */}
              <ThemeToggle />

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary text-primary-foreground font-medium">
                        {user?.email?.split("@")[0]?.split(".").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2) || "U"}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">
                        {user?.email?.split("@")[0]?.replace(/[._]/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "User"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setLocation("/profile")}>
                    <UserCircle className="mr-2 h-4 w-4" />
                    Profile & Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => {
                    logout();
                    setLocation("/login");
                  }}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Chat Messages - Custom Scrollbar - Responsive */}
          <div ref={chatContainerRef} className="flex-1 chat-scroll p-2 sm:p-4 overflow-y-auto relative min-h-0">
            <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
              {messages.length <= 1 ? (
                /* Welcome Screen - ChatGPT Style - Responsive */
                <div className="flex flex-col items-center justify-center h-full min-h-[50vh] sm:min-h-[60vh] text-center px-4">
                  <div className="mb-6 sm:mb-8">
                    <img
                      src="/veritus-icon.png"
                      alt="Veritus"
                      className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl object-cover mb-4 sm:mb-6 mx-auto"
                    />
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold mb-3 sm:mb-4 text-foreground">
                      What can I help you analyze today?
                    </h1>
                    <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto px-4">
                      I'm your strategic intelligence engine. Ask me about business strategies, market analysis, competitive landscapes, or any strategic question.
                    </p>
                  </div>
                  
                  {/* Quick Start Examples - Responsive */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-3xl w-full px-2">
                    <button
                      onClick={() => setInput("Analyze the competitive landscape for AI-powered fintech startups in the UK market")}
                      className="p-3 sm:p-4 rounded-xl bg-card hover:bg-muted border border-card-border hover:border-border transition-all text-left group"
                    >
                      <div className="flex items-center gap-2 sm:gap-3 mb-2">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                          <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                        </div>
                        <span className="font-medium group-hover:text-foreground text-sm sm:text-base">Market Analysis</span>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground group-hover:text-foreground">
                        Analyze competitive landscapes and market opportunities
                      </p>
                    </button>
                    
                    <button
                      onClick={() => setInput("Create a comprehensive investment thesis for a SaaS startup targeting small businesses")}
                      className="p-3 sm:p-4 rounded-xl bg-card hover:bg-muted border border-card-border hover:border-border transition-all text-left group"
                    >
                      <div className="flex items-center gap-2 sm:gap-3 mb-2">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                          <FileText className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                        </div>
                        <span className="font-medium group-hover:text-foreground text-sm sm:text-base">Investment Research</span>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground group-hover:text-foreground">
                        Build investment theses and financial projections
                      </p>
                    </button>
                    
                    <button
                      onClick={() => setInput("Develop a go-to-market strategy for a B2B AI platform")}
                      className="p-3 sm:p-4 rounded-xl bg-card hover:bg-muted border border-card-border hover:border-border transition-all text-left group"
                    >
                      <div className="flex items-center gap-2 sm:gap-3 mb-2">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                          <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                        </div>
                        <span className="font-medium group-hover:text-foreground text-sm sm:text-base">Strategy Planning</span>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground group-hover:text-foreground">
                        Create strategic plans and business models
                      </p>
                    </button>
                    
                    <button
                      onClick={() => setInput("Conduct a risk analysis for entering the European renewable energy market")}
                      className="p-3 sm:p-4 rounded-xl bg-card hover:bg-muted border border-card-border hover:border-border transition-all text-left group"
                    >
                      <div className="flex items-center gap-2 sm:gap-3 mb-2">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                          <Search className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                        </div>
                        <span className="font-medium group-hover:text-foreground text-sm sm:text-base">Risk Assessment</span>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground group-hover:text-foreground">
                        Evaluate risks and regulatory challenges
                      </p>
                    </button>
                  </div>
                </div>
              ) : (
                /* Chat Messages */
                messages.map((m: any) => (
                  <div key={m.id}>
                    {m.role === "system" && (
                      <div className="text-center">
                        <div className="inline-block bg-gray-800/40 px-4 py-3 rounded-2xl text-sm text-gray-300 max-w-2xl whitespace-pre-wrap border border-gray-700/20">
                          {m.text}
                        </div>
                      </div>
                    )}
                    {m.role === "user" && (
                      <div className="flex justify-end mb-3 sm:mb-4">
                        <div className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-2xl max-w-[85%] sm:max-w-2xl bg-primary/12 border border-primary/30 text-foreground">
                          <div className="text-sm sm:text-[15px] leading-relaxed">{m.text}</div>
                        </div>
                      </div>
                    )}
                    {m.role === "assistant" && (
                      <div className="flex justify-start mb-3 sm:mb-4">
                        <div className="max-w-[95%] sm:max-w-3xl">
                          {/* AI Avatar */}
                          <div className="flex items-start gap-2 sm:gap-3">
                            <img
                              src="/veritus-icon.png"
                              alt="Veritus"
                              className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover flex-shrink-0 mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <pre className="whitespace-pre-wrap text-sm sm:text-[15px] text-foreground leading-relaxed font-sans">{m.text}</pre>
                              {m.meta && (
                                <div className="flex items-center justify-between mt-3">
                                  <div className="text-xs text-gray-500">
                                    {m.meta.mode?.replace('-', ' ')} • {new Date(m.time).toLocaleTimeString()}
                                  </div>
                                  <div className="flex gap-2">
                                    <button 
                                      onClick={() => navigator.clipboard.writeText(m.text)}
                                      className="text-xs text-gray-500 hover:text-gray-300 transition-colors p-1 rounded hover:bg-gray-800/50"
                                      title="Copy response"
                                    >
                                      📋
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
              {loading && (
                <div className="flex justify-start">
                  <div className="max-w-3xl">
                    <div className="flex items-start gap-3">
                      <img
                        src="/veritus-icon.png"
                        alt="Veritus"
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-1"
                      />
                      <div className="flex items-center gap-3 mt-1">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                        <span className="text-[15px] text-gray-300">Analyzing and generating insights...</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={scrollRef} />
            </div>
            
            {/* Scroll to Bottom Button - Responsive */}
            {!autoScroll && (
              <button
                onClick={scrollToBottom}
                className="absolute bottom-16 sm:bottom-20 right-4 sm:right-8 bg-gray-800/90 hover:bg-gray-700 text-white p-2 sm:p-3 rounded-full shadow-lg transition-all duration-200 border border-gray-600/50"
                title="Scroll to bottom"
              >
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}
          </div>

          {/* Input Area - More Prominent - Responsive */}
          <div className="p-3 sm:p-6 bg-transparent">
            <div className="max-w-4xl mx-auto">
              <form onSubmit={handleSubmit}>
                <div className="relative flex items-center bg-[#0b0f1a]/60 rounded-xl sm:rounded-2xl border-2 border-primary/30 shadow-2xl backdrop-blur-sm hover:border-primary/50 transition-all duration-200">
                  {/* Mode Selector - Hidden on small screens */}
                  <div className="hidden sm:flex items-center gap-2 px-3 sm:px-4 py-3 border-r border-gray-700/30">
                    <select
                      value={mode}
                      onChange={(e) => setMode(e.target.value)}
                      className="bg-transparent text-xs sm:text-sm text-gray-300 border-none outline-none cursor-pointer"
                      title="Select analysis mode"
                      aria-label="Select analysis mode"
                    >
                      <option value="quick-scan">Quick</option>
                      <option value="balanced">Balanced</option>
                      <option value="deep-dive">Deep</option>
                    </select>
                  </div>

                  {/* Input Field - Responsive */}
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask me about your business strategy, market analysis, or any business question..."
                    className="flex-1 bg-transparent border-none outline-none text-foreground placeholder-gray-400 px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-normal mobile-input"
                    disabled={loading}
                  />

                  {/* Action Buttons - Responsive */}
                  <div className="flex items-center gap-1 px-1 sm:px-2">
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      onChange={handleFileUpload}
                      accept=".txt,.pdf,.doc,.docx,.csv,.json"
                      title="Upload file"
                      aria-label="Upload file"
                    />
                    <button
                      type="button"
                      onClick={() => document.getElementById("file-upload")?.click()}
                      className="hidden sm:block p-2 text-gray-400 hover:text-gray-200 transition-colors rounded-lg hover:bg-gray-700/50"
                      title="Upload file"
                    >
                      <Upload className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={handleVoiceInput}
                      className={`hidden sm:block p-2 transition-colors rounded-lg hover:bg-gray-700/50 ${
                        isListening ? "text-red-400 animate-pulse" : "text-gray-400 hover:text-gray-200"
                      }`}
                      title={isListening ? "Listening..." : "Voice input"}
                    >
                      <Mic className="w-4 h-4" />
                    </button>
                    <button
                      type="submit"
                      disabled={!input.trim() || loading}
                      className="p-2 sm:p-2.5 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors ml-1"
                      title="Send message"
                    >
                      <Send className="w-4 h-4 text-black" />
                    </button>
                  </div>
                </div>
              </form>
              
              {/* Footer Text - Responsive */}
              <p className="text-xs sm:text-sm text-gray-400 text-center mt-2 sm:mt-3 px-2">
                💡 <strong>Tip:</strong> <span className="hidden sm:inline">Type your business question above or click the example buttons to get started with Veritus analysis</span><span className="sm:hidden">Ask me anything about business strategy</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ChatGPT-Style Search Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center pt-[10vh]">
          <div className="bg-card rounded-xl border border-card-border shadow-2xl w-full max-w-2xl mx-4 max-h-[70vh] flex flex-col">
            {/* Search Header */}
            <div className="p-4 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search chats..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-input text-foreground pl-12 pr-4 py-3 rounded-lg border border-input outline-none text-base focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-colors placeholder-muted-foreground"
                  autoFocus
                />
                <button
                  onClick={() => setShowSearchModal(false)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-md hover:bg-muted transition-colors"
                  title="Close search"
                  aria-label="Close search"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            </div>

            {/* Search Results */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {(() => {
                const filteredChats = chatHistory.filter((chat: any) => 
                  chat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  chat.preview.toLowerCase().includes(searchQuery.toLowerCase())
                );

                if (searchQuery === "") {
                  return (
                    <div className="p-6 text-center">
                      <Search className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <h3 className="text-lg font-medium text-foreground mb-2">Search your chats</h3>
                      <p className="text-muted-foreground text-sm">
                        Find previous conversations by typing keywords
                      </p>
                      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                        <kbd className="px-2 py-1 bg-muted rounded">↑</kbd>
                        <kbd className="px-2 py-1 bg-muted rounded">↓</kbd>
                        <span>to navigate</span>
                        <kbd className="px-2 py-1 bg-muted rounded">↵</kbd>
                        <span>to select</span>
                        <kbd className="px-2 py-1 bg-muted rounded">esc</kbd>
                        <span>to close</span>
                      </div>
                    </div>
                  );
                }

                if (filteredChats.length === 0) {
                  return (
                    <div className="p-6 text-center">
                      <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <h3 className="text-lg font-medium text-foreground mb-2">No chats found</h3>
                      <p className="text-muted-foreground text-sm">
                        Try different keywords or check your spelling
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="p-2">
                    {filteredChats.map((chat: any, index: number) => (
                      <button
                        key={chat.id}
                        onClick={() => {
                          loadChat(chat);
                          setShowSearchModal(false);
                          setSearchQuery("");
                        }}
                        className="w-full flex flex-col items-start gap-2 p-4 hover:bg-muted rounded-lg transition-colors text-left group border border-transparent hover:border-border"
                      >
                        <div className="flex items-center gap-3 w-full">
                          <MessageSquare className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <div className="font-medium text-foreground group-hover:text-foreground truncate flex-1">
                            {chat.title}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {chat.timestamp}
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground group-hover:text-foreground line-clamp-2 ml-7">
                          {chat.preview}
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Search Footer */}
            <div className="px-4 py-3 border-t border-gray-700/30 bg-gray-900/50">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center gap-4">
                  <span>{chatHistory.length} total chats</span>
                  {searchQuery && (
                    <span>
                      {chatHistory.filter((chat: any) => 
                        chat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        chat.preview.toLowerCase().includes(searchQuery.toLowerCase())
                      ).length} matches
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-800 rounded">esc</kbd>
                  <span>to close</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      <FeedbackModal 
        isOpen={showFeedbackModal} 
        onClose={() => setShowFeedbackModal(false)}
        currentRoute="/research-chat"
      />
    </>
  );
}
